import React from 'react';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isAxiosError } from 'axios';
import 'main.css';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router';

import { initializeGeneratedApiClient } from 'api/generatedClient';
import App from './App.tsx';

initializeGeneratedApiClient();

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,

  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,

  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      // Additional SDK configuration goes in here, for example:
      colorScheme: 'light',
      useSentryUser: {
        name: 'fullName',
        email: 'email',
      },
    }),
  ],

  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions

  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/^https:\/\/api\.drivingdoc\.com/],

  // Session Replay
  replaysSessionSampleRate: 0.0, // This sets the sample rate at 0%.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isAxiosError(error) && error?.status && error?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
        <HelmetProvider>
          <Sentry.ErrorBoundary fallback={<p>Something went wrong.</p>} showDialog>
            <App />
          </Sentry.ErrorBoundary>
        </HelmetProvider>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      </LocalizationProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
